import { useLazyQuery, useQuery } from '@apollo/client';
import { useState } from 'react';
import { History, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { profileQueries, type ProfileSection } from './queries';
import type { ProfileData, ProfileEntity, ProfileRecord, ProfileValue } from './types';
import { ProfileRecordPager } from './ProfileRecordPager';
import { profileData } from './profileData';

export const ProfileRecordHistory = ({ odrnumber, section, entity, canWrite, onUseValues, onUseAlias }: {
  odrnumber: number; section: ProfileSection; entity: ProfileEntity; canWrite: boolean;
  onUseValues: (current: ProfileRecord, values: ProfileValue[]) => void;
  onUseAlias?: (values: ProfileValue[]) => void;
}) => {
  const result = useQuery<Record<string, ProfileData>>(profileQueries[section.key], {
    variables: { odrnumber, entity: entity.name, history: true }, fetchPolicy: 'network-only',
  });
  const data = profileData(result.data?.[`participantProfile${section.key}`], odrnumber);
  const group = data?.entities?.find(group => group.name === entity.name);
  const [loadCurrent, loadingCurrent] = useLazyQuery<Record<string, ProfileData>>(profileQueries[section.key], { fetchPolicy: 'network-only' });
  const [error, setError] = useState<string | null>(null);
  const handleUseValues = async (record: ProfileRecord) => {
    setError(null);
    const current = entity.records.find(item => item.key === record.key);
    if (current) { onUseValues(current, record.values); return; }
    try {
      const response = await loadCurrent({ variables: { odrnumber, entity: entity.name, key: record.key } });
      if (response.error) throw response.error;
      const selected = profileData(response.data?.[`participantProfile${section.key}`], odrnumber)?.entities[0]?.records[0];
      if (!selected) throw new Error('The current record is unavailable for restoration.');
      onUseValues(selected, record.values);
    } catch (error) { setError(error instanceof Error ? error.message : 'Unable to load the current record.'); }
  };
  return <div className="space-y-4">
    {result.loading && <p role="status">Loading history…</p>}
    {result.error && <p role="alert">{result.error.message}</p>}
    {!result.loading && !result.error && !data && <p role="alert">History review fields are unavailable.</p>}
    {error && <p role="alert">{error}</p>}
    {data?.blocked && <p role="alert">History is unavailable for this participant.</p>}
    {!result.loading && !result.error && !data?.blocked && !group?.records.length && <p>No history is available.</p>}
    {group && !result.loading && !result.error && !data?.blocked && <ProfileRecordPager odrnumber={odrnumber} section={section} initial={group} history>{page => page.records.map(record => {
      return <section key={record.revision} className="space-y-2 border-b pb-4 last:border-0">
        <p>{record.timestamp} · {record.author} · {record.removed ? 'Archived' : record.canonical ? 'Current source version' : 'Previous version'}</p>
        <dl className="grid gap-3 md:grid-cols-2">{entity.fields.map(field => <div key={field.name}><dt className="font-medium">{field.label}</dt><dd className="whitespace-pre-wrap break-words">{record.values.find(value => value.name === field.name)?.value ?? 'Not recorded'}</dd></div>)}</dl>
        {canWrite && !record.removed && record.canonical === false && <Button id={`profile-history-${entity.name}-${record.revision}-use`} variant="outline" disabled={loadingCurrent.loading} onClick={() => { void handleUseValues(record); }}><History aria-hidden="true" className="mr-2 h-4 w-4" />Use these values</Button>}
        {canWrite && onUseAlias && !record.removed && record.canonical === false && <Button id={`profile-history-${entity.name}-${record.revision}-alias`} variant="outline" onClick={() => onUseAlias(record.values)}><UserPlus aria-hidden="true" className="mr-2 h-4 w-4" />Use as alias</Button>}
      </section>;
    })}</ProfileRecordPager>}
  </div>;
};
